// Stable analytics identifiers for the button that starts a pricing journey.
export const PRICING_ENTRY_POINTS = [
  'create_project_queue_limit',
  'home_demo_project',
  'mobile_create_project',
  'mobile_editor_header_upgrade',
  'mobile_results_upgrade',

  'sidebar_view_plans',
  'sidebar_trial_banner',
  'sidebar_api_banner',
  'create_project_upgrade_link',
  'create_recording_upgrade_link',
  'account_trial_upgrade',
  'after_purchase_view_plans',
  'api_key_upgrade',
  'buy_minutes_upgrade',
  'create_first_project_upgrade',
  'create_project_dictionary',
  'create_project_prompting',
  'create_project_srt_upload',
  'create_project_limit_banner',
  'create_project_limit_upgrade',
  'create_teamspace_upgrade',
  'credits_panel_upgrade',
  'credits_upgrade',
  'dictionary_library_create',
  'direct_pricing',
  'discount_banner_upgrade',
  'editor_advanced_tools_upgrade',
  'editor_apply_changes',
  'editor_dictionary',
  'editor_download',
  'editor_download_audio',
  'editor_download_captions',
  'editor_download_lipsync',
  'editor_download_srt',
  'editor_generate_lipsync_captions',
  'editor_generate_translated_captions',
  'editor_lipsync',
  'editor_lipsync_alert',
  'editor_locked_segment',
  'editor_multilanguage',
  'editor_prompting',
  'editor_srt_upload',
  'folder_download_all',
  'general_paywall_view_plans',
  'header_upgrade',
  'home_minutes_upgrade',
  'localization_limits_upgrade',
  'manage_boosters_upgrade',
  'mobile_results_download',
  'onboarding_auto',
  'paid_trial_plan_features',
  'trial_paywall_experiment',
  'payment_required_redirect',
  'player_lipsync_offer',
  'player_localization_offer',
  'player_overlay_lipsync',
  'player_overlay_upgrade',
  'project_menu_download',
  'project_player_download',
  'project_player_download_audio',
  'project_player_download_captions',
  'project_player_download_lipsync',
  'project_player_download_srt',
  'project_player_generate_lipsync_captions',
  'project_player_generate_translated_captions',
  'queue_paywall_view_plans',
  'recording_editor_view_plans',
  'recording_paywall_auto_zoom',
  'recording_paywall_download',
  'recording_paywall_recording_minutes',
  'recording_paywall_translate_minutes',
  'recording_share_upgrade',
  'sidebar_create_teamspace',
  'sidebar_teamspace_limit',
  'subscription_expiry_banner_upgrade',
  'subscription_panel_upgrade',
  'subscription_panel_view_plans',
  'teamspace_limits_upgrade',
  'teamspace_settings_add_seat',
  'teamspaces_not_active_upgrade',
  'trial_banner_upgrade',
  'trial_preview_download',
  'trial_preview_download_audio',
  'trial_preview_download_captions',
  'trial_preview_download_lipsync',
  'trial_preview_download_srt',
  'trial_preview_export',
  'trial_preview_generate_lipsync_captions',
  'trial_preview_generate_translated_captions',
  'trial_preview_header_unlock',
  'trial_preview_player_full_video',
  'trial_preview_player_lipsync',
  'trial_preview_player_see_plans',
  'trial_preview_what_next_ai_rewrite',
  'trial_preview_what_next_audio_download',
  'trial_preview_what_next_captions',
  'trial_preview_what_next_dictionary',
  'trial_preview_what_next_download',
  'trial_preview_what_next_full_video',
  'trial_preview_what_next_lipsync',
  'trial_preview_what_next_multilanguage',
  'trial_preview_what_next_plans',
  'trial_preview_what_next_prompting',
  'trial_preview_what_next_srt_download',
  'trial_preview_what_next_srt_upload',
  'trial_preview_what_next_subtitle_export',
  'trial_preview_what_next_teamspace',
  'trial_preview_what_next_unlock',
  'trial_preview_what_next_voice_library',
  'trial_share_upgrade',
  'unattributed_paywall',
  'visual_translation_home_card',
  'visual_translation_tab',
  'visual_translation_upload_toggle',
  'voice_library_create',
  'voice_library_create_instant',
  'voice_library_empty_create',
  'voice_settings_save',
  'voice_settings_upgrade',
] as const;

export type PricingEntryPoint = (typeof PRICING_ENTRY_POINTS)[number];
export type PricingAction = 'open_pricing' | 'start_purchase';
export type PricingAnalyticsProperties = {
  pricing_action: PricingAction;
  pricing_entry_point: PricingEntryPoint;
};

// Pricing journeys started from the screen recorder: its paywalls, the recording editor and the recorder's upgrade link.
export const isRecordingPricingEntryPoint = (entryPoint: PricingEntryPoint): boolean =>
  entryPoint.startsWith('recording_') || entryPoint === 'create_recording_upgrade_link';

export const getPricingEntryPoint = (state: unknown): PricingEntryPoint => {
  const entryPoint = (state as { pricingEntryPoint?: unknown } | null)?.pricingEntryPoint;
  return PRICING_ENTRY_POINTS.find((value) => value === entryPoint) ?? 'direct_pricing';
};

export const getPricingAnalyticsProperties = (
  entryPoint: PricingEntryPoint,
  action: PricingAction = 'start_purchase',
): PricingAnalyticsProperties => ({ pricing_action: action, pricing_entry_point: entryPoint });

const TRIAL_PREVIEW_FEATURE_ENTRIES = {
  aiRewrite: 'trial_preview_what_next_ai_rewrite',
  audioDownload: 'trial_preview_what_next_audio_download',
  download: 'trial_preview_what_next_download',
  glossary: 'trial_preview_what_next_dictionary',
  lipSync: 'trial_preview_what_next_lipsync',
  multilanguage: 'trial_preview_what_next_multilanguage',
  srtDownload: 'trial_preview_what_next_srt_download',
  srtUpload: 'trial_preview_what_next_srt_upload',
  subtitleExport: 'trial_preview_what_next_subtitle_export',
  teamspace: 'trial_preview_what_next_teamspace',
  translationDictionary: 'trial_preview_what_next_dictionary',
  translationTone: 'trial_preview_what_next_prompting',
  unlimitedLength: 'trial_preview_what_next_full_video',
  userPrompting: 'trial_preview_what_next_prompting',
  videoSubtitles: 'trial_preview_what_next_captions',
  voicePresets: 'trial_preview_what_next_voice_library',
} as const;

export const getTrialPreviewFeatureEntryPoint = (
  feature: keyof typeof TRIAL_PREVIEW_FEATURE_ENTRIES,
): PricingEntryPoint => TRIAL_PREVIEW_FEATURE_ENTRIES[feature];

export const getDownloadPricingEntryPoint = (
  source: 'trialProjectEditor' | 'trialProjectPreview' | undefined,
  action:
    | 'download'
    | 'download_audio'
    | 'download_captions'
    | 'download_lipsync'
    | 'download_srt'
    | 'generate_lipsync_captions'
    | 'generate_translated_captions',
): PricingEntryPoint => {
  let surface: 'editor' | 'project_player' | 'trial_preview' = 'project_player';
  if (source === 'trialProjectEditor') surface = 'editor';
  if (source === 'trialProjectPreview') surface = 'trial_preview';
  return `${surface}_${action}`;
};

export const getTrialDownloadEntryPoint = (
  source: 'trialProjectEditor' | 'trialProjectPreview' | undefined,
  optionId: string,
): PricingEntryPoint => {
  const actions = {
    '1': 'download',
    '2': 'download_captions',
    '3': 'download_audio',
    '4': 'download_lipsync',
    '5': 'download_srt',
  } as const;
  return getDownloadPricingEntryPoint(source, actions[optionId as keyof typeof actions] ?? 'download');
};
